import { isEqual, xor } from 'lodash-es'
import mutations from './filter-mutations'
import actions from './filter-actions'
import { SORTING_OPTIONS, FILTER_TYPE, FILTER_LEVEL } from '@/components/FilterPage/constants'
import { filterIsGender, filterIsType, setOnlyFullRows, newPageInfo } from './filter-store-helpers'

function findSubPage(subPages, activeFilters) {
  const filtersObj = activeFilters.reduce(
    (acc, item) => ({
      ...acc,
      [item.id]: item.values,
    }),
    {}
  )
  return subPages.find(page => isEqual(page.filterValues, filtersObj))
}

export default {
  namespaced: true,

  state: {
    products: [],
    promoCards: [],
    promoPositions: [], // default positions
    filters: [],
    activeFilters: [],
    sortOptions: SORTING_OPTIONS,
    activeSort: {},
    subPages: [],
    basePath: '',
    topBanner: {},
    featuredArticles: {},
    hasExplodedVariants: false,
    hasArticlesPageBottom: false,
    isStickyMenuMounted: false,
    stickyMenuComponent: null,
    pageInfo: newPageInfo(),
    isFiltersFetched: false,
    areProductsFetched: false,
    isFetchingMoreProducts: false,
    isPromoCardsFetched: false,
    isSubPagesFetched: false,
    isDataFetched: false,
    isUpdatedByFilter: false,
  },

  mutations: mutations,

  actions: actions,

  getters: {
    activeSubPage: state => {
      const { subPages = [], activeFilters } = state

      const fallBackFilters = activeFilters.filter(
        filter => filter.id === 'gender' || filter.id === 'product'
      )

      // Find the best match:
      // 1. Look for a 100% match
      // 2. If not, try to match gender AND product
      // 3. If not, try to match gender OR product
      // 4. If not, serve the most generic content (no filters)
      return (
        findSubPage(subPages, activeFilters) ||
        findSubPage(subPages, fallBackFilters) ||
        findSubPage(subPages, [])
      )
    },

    primaryFilters: state => state.filters.filter(filter => filter.order === FILTER_TYPE.PRIMARY),

    activePrimaryFilters: state =>
      state.activeFilters.filter(filter => filter.order === FILTER_TYPE.PRIMARY),

    secondaryFilters: state =>
      state.filters.filter(filter => filter.order === FILTER_TYPE.SECONDARY),

    activeSecondaryFilters: state =>
      state.activeFilters.filter(filter => filter.order === FILTER_TYPE.SECONDARY),

    pathFilters: state => state.filters.filter(filter => filter.includedInPath),

    paramsFilters: (state, getters) =>
      getters.activeSecondaryFilters.filter(filter => filter.includedInPath),

    queryFilters: (state, getters) =>
      getters.activeSecondaryFilters.filter(filter => !filter.includedInPath),

    activeFiltersValues: state => state.activeFilters.map(filter => filter.values).flat(),

    activeItemFiltersQuery: state =>
      state.activeFilters
        .filter(filter => filter.level === FILTER_LEVEL.ITEM)
        .map(filter => ({
          name: filter.id,
          values: filter.values,
        })),

    activeVariantFiltersQuery: state =>
      state.activeFilters
        .filter(filter => filter.level === FILTER_LEVEL.VARIANT)
        .map(filter => ({
          name: filter.type === FILTER_TYPE.SIZE ? filter.type : filter.id,
          values: filter.values,
        })),

    visibleSecondaryFilters: (state, getters) => {
      return getters.secondaryFilters.filter(filter => {
        let keepItem = true
        for (const key in filter.excludeIf) {
          // if this condition is not valid for this filter page
          // e.g 'product_classics' is not relevant for /explore or /collection pages
          if (!getters.primaryFilters.find(filter => filter.id === key)) continue

          const conditions = filter.excludeIf[key] // filter.excludeIf.gender = ['mens','']
          const activeFilterValues = getters.activePrimaryFilters.find(active => active.id === key)
            ?.values || [''] // ['mens', ''] || ['']

          keepItem = !conditions.some(condition => activeFilterValues.includes(condition))

          // if a single active filter fails the validation
          // skip all further validations exiting the for()
          if (!keepItem) break
        }
        return keepItem
      })
    },

    hasPromoCards: state => state.promoCards.length > 0,

    filteredPromoCards: (state, getters) => {
      /**
       * Filters promoCards by:
       * - mandatoryFilterValues: IF set, all values must match with active filters
       * - filterValues: IF set, the values must match IF some that filter is active, otherwise it is visible
       * - IF no filter is set, the card is always visible
       */
      return state.promoCards.filter(card => {
        if (Object.keys(card.mandatoryFilterValues || {}).length) {
          if (!getters.activeFiltersValues.length) return false

          const cardMandatoryFilterValues = Object.values(card.mandatoryFilterValues).flat()
          return !xor(getters.activeFiltersValues, cardMandatoryFilterValues).length
        }

        if (Object.keys(card.filterValues || {}).length && getters.activeFiltersValues.length) {
          const cardFilterValues = Object.values(card.filterValues).flat()
          return cardFilterValues.some(value => getters.activeFiltersValues.includes(value))
        }

        return true
      })
    },

    hasActiveFilters: state => state.activeFilters.length > 0,

    activeGender: state => state.activeFilters.find(filterIsGender)?.values[0] || '',

    activeProductType: state => state.activeFilters.find(filterIsType)?.values[0] || '',

    shuffledProductsPromos: (state, getters) => {
      /**
       * Shuffle with promo cards
       *
       * IF has promo cards, iterate over them and check:
       * - IF promo card has a matching product, insert after that product
       * - ELSE insert the promo card on a default position IF
       *   the length of products is equal or bigger than its position
       */
      const shuffledProductsPromos = [...state.products]

      if (getters.hasPromoCards) {
        getters.filteredPromoCards.forEach((promo, i) => {
          const matchIndex = shuffledProductsPromos.findIndex(
            product =>
              product.productGroup === promo.productGroup &&
              !!product.productGroup &&
              !!promo.productGroup
          )
          const orderIndex = matchIndex > -1 ? matchIndex : state.promoPositions[i]
          if (orderIndex <= state.products.length)
            shuffledProductsPromos.splice(orderIndex, 0, promo)
        })
      }

      // Hides exceeding items IF not the last page
      if (state.pageInfo.hasNextPage) setOnlyFullRows(shuffledProductsPromos)

      return shuffledProductsPromos.map((item, i) => ({ ...item, cardIndex: i })) // add index for transitions
    },

    isMobile: (state, getters, rootState, rootGetters) => !rootGetters['viewport/isMqLarger'],
  },
}
